<template>
    <div>
        <FullCalendar
            ref="calendar"
            class="calendar-app-calendar ppv2-bg-white ppv2-rounded-xl ppv2-p-4"
            :options="calendarOptions"
        >
            <template v-slot:dayCellContent="arg">
                <div class="calendar-day-cell-content">
                    <span>{{ arg.dayNumberText }}</span>
                    <span
                        class="edit-day-icon-global"
                        title="Éditer la journée"
                        aria-label="Éditer la journée"
                        @click.stop="editDay(getDateString(arg))"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                            <path
                                d="M15.232 5.232a2.5 2.5 0 0 1 0 3.536l-7.5 7.5A2 2 0 0 1 6 17H4a1 1 0 0 1-1-1v-2c0-.53.21-1.039.586-1.414l7.5-7.5a2.5 2.5 0 0 1 3.536 0zm-2.828 2.828l-7.5 7.5A.997.997 0 0 0 4 15v2h2c.265 0 .52-.105.707-.293l7.5-7.5a.5.5 0 0 0-.707-.707z"
                            />
                        </svg>
                    </span>
                </div>
            </template>

            <template v-slot:eventContent="arg">
                <span class="event-content">
                    <b class="calendar-event-title">{{ arg.event.title }}</b>
                    <span class="edit-icon" title="Éditer">🖉</span>
                </span>
            </template>
        </FullCalendar>
    </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import frLocale from '@fullcalendar/core/locales/fr'
import EstablishmentManager from '../../../helpers/ApiEstablishmentManager'

export default {
    name: 'PlanningCalendar',
    components: { FullCalendar },
    data() {
        return {
            apiDays: [],
            establishmentId: 1,
            calendarOptions: {
                plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
                initialView: 'dayGridMonth',
                locale: frLocale,
                headerToolbar: {
                    left: 'prev',
                    center: 'title',
                    right: 'next',
                },
                dayMaxEvents: true,
                height: 'auto',
                events: [],
                dayCellDidMount: this.attachDayClick,
                datesSet: () => {
                    this.$nextTick(() => {
                        this.fetchCalendarDays()
                    })
                },
            },
        }
    },
    mounted() {
        // Use $nextTick to ensure the calendar is rendered before accessing it
        this.$nextTick(() => {
            this.fetchCalendarDays()
        })

        // Add event listener for locale changes
        window.addEventListener('storage', this.handleStorageChange)
    },
    beforeDestroy() {
        // Clean up the event listener when component is destroyed
        window.removeEventListener('storage', this.handleStorageChange)
    },
    watch: {
        '$i18n.locale'() {
            // Update calendar when i18n locale changes
            this.updateCalendarEvents()
        },
    },
    methods: {
        handleStorageChange(event) {
            // Check if the changed item is 'locale'
            if (event.key === 'locale') {
                // Update calendar when locale changes in localStorage
                this.updateCalendarEvents()
            }
        },
        attachDayClick(info) {
            info.el.style.cursor = 'pointer'
            info.el.addEventListener('click', () => {
                const clickedDateStr = this.formatDateLocal(info.date)
                this.editDay(clickedDateStr)
            })
        },
        getDateString(arg) {
            const date = arg.date || new Date(arg.dateStr)
            return this.formatDateLocal(date)
        },
        formatDateLocal(date) {
            return (
                date.getFullYear() +
                '-' +
                String(date.getMonth() + 1).padStart(2, '0') +
                '-' +
                String(date.getDate()).padStart(2, '0')
            )
        },
        editDay(dateStr) {
            const dayData = this.apiDays.find((d) => {
                return d.date === dateStr
            })
            this.$emit('edit-day', dayData)
        },
        /**
         * Structure the API response into a more usable format
         * @param {Array} apiResponse - Raw API response array
         * @returns {Array} - Structured days array
         */
        structureDaysData(apiResponse) {
            return Object.values(apiResponse).map((day) => ({
                date: day.date,
                day: day.day,
                isDefault: day.is_default,
                isOpen: Boolean(day.is_open),
                periods: day.start && day.end ? [{ start: day.start, end: day.end }] : [],
                lunches: day.lunch_start && day.lunch_end ? [{ start: day.lunch_start, end: day.lunch_end }] : [],
                label: day.label,
            }))
        },

        async fetchCalendarDays() {
            try {
                if (!this.$refs.calendar) {
                    console.warn('Calendar ref not found')
                    return
                }

                // Get the current view of the calendar
                const calendarApi = this.$refs.calendar.getApi()

                // Get the current view's date range
                const view = calendarApi.view
                const start = view.currentStart.toISOString().split('T')[0]
                const end = view.currentEnd.toISOString().split('T')[0]

                const response = await EstablishmentManager.getScheduling({
                    start,
                    end,
                    establishmentId: this.establishmentId,
                })

                // Transform the API response to the desired structure
                this.apiDays = this.structureDaysData(response?.data || [])
                this.updateCalendarEvents()
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.apiDays = []
                this.updateCalendarEvents()
            }
        },
        updateCalendarEvents() {
            if (!this.$refs.calendar) return
            const calendarApi = this.$refs.calendar.getApi()

            const newEvents = this.generateCalendarEvents()

            // Update your Vue state so attachDayClick and others see the events
            this.calendarOptions.events = newEvents

            calendarApi.removeAllEvents()
            calendarApi.addEventSource(newEvents)

            // Now you can safely update the day cell backgrounds
            this.updateDayCellBackgrounds()
        },
        generateCalendarEvents() {
            const events = []

            // Convert to array if it's a Vue Observer or an object

            this.apiDays.forEach((day) => {
                const { date, periods = [], lunches = [] } = day
                if (day.isOpen == false) {
                    events.push({
                        title: 'Fermé',
                        start: date,
                        allDay: true,
                        color: '#f1f5f9',
                        textColor: '#64748b',
                        isDefault: day.isDefault,
                        type: 'period',
                    })
                } else {
                    periods.forEach(({ start, end }) => {
                        events.push({
                            title: `${start} - ${end}`,
                            start: date,
                            allDay: true,
                            color: '#fff',
                            textColor: '#000',
                            isDefault: day.isDefault,
                            type: 'period',
                        })
                    })
                }

                lunches.forEach(({ start, end }) => {
                    if (start && end && start != end && day.isOpen == true) {
                        events.push({
                            title: `${this.$t('resource_planning.lunch_break')} (${start} - ${end})`,
                            start: date,
                            allDay: true,
                            color: '#fef3c7',
                            textColor: '#92400e',
                            type: 'lunch',
                        })
                    }
                })
            })

            return events
        },

        updateDayCellBackgrounds() {
            this.$nextTick(() => {
                if (!this.$refs.calendar) return

                const calendarEl = this.$refs.calendar.$el || this.$refs.calendar.$el || this.$refs.calendar
                // sometimes refs differ, adjust if needed

                // Select all day cells (adjust selector if you use a different view)
                const dayCells = calendarEl.querySelectorAll('.fc-daygrid-day')

                // Unwrap events array safely
                const events =
                    this.calendarOptions.events && this.calendarOptions.events.slice ? this.calendarOptions.events.slice() : []
                dayCells.forEach((dayCell) => {
                    const dateStr = dayCell.getAttribute('data-date')

                    // Check if any event on this date has isDefault === false and type === 'period'
                    const hasCustomEvent = events.some((event) => {
                        if (!event.start) return false
                        const eventDateStr = new Date(event.start).toISOString().split('T')[0]
                        return eventDateStr === dateStr && event.isDefault === false && event.type === 'period'
                    })

                    if (hasCustomEvent) {
                        dayCell.style.backgroundColor = '#99B3FF' // yellow
                    }
                })
            })
        },
    },
}
</script>

<style scoped>
.fc-daygrid-day.fc-day-today {
    background: #e0f2fe !important;
    border-radius: 8px;
}

.fc-daygrid-day {
    border-radius: 8px;
}

.fc-daygrid-event {
    font-size: 0.95em;
    padding: 0.2em 0.4em;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.calendar-day-cell-content {
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    height: 100%;
}

.edit-day-icon-global {
    position: absolute;
    left: 4px;
    bottom: -50px;
    cursor: pointer;
    font-size: 1em;
    color: #64748b;
    opacity: 0.85;
    background: #fff;
    border-radius: 50%;
    padding: 2px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition:
        color 0.2s,
        opacity 0.2s,
        background 0.2s;
}

.edit-day-icon-global:hover {
    color: #2563eb;
    opacity: 1;
    background: #f1f5f9;
}

.calendar-event-title {
    color: #0f172a;
    font-weight: bold;
}

.event-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.edit-icon {
    color: #64748b;
    margin-left: 2px;
    transition: color 0.2s;
    cursor: pointer;
}

.edit-icon:hover {
    color: #2563eb;
}

.fc .fc-daygrid-day.fc-day-disabled,
.fc .fc-daygrid-day.fc-day-other {
    background: #f8fafc !important;
}

::v-deep(.fc .fc-daygrid-event-harness) {
    background: #fff !important;
}
</style>
